import React, { Component } from 'react';
import { Link } from 'react-router';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cn from 'classnames';

import { HYDRA_6PM_MARTY_RETURNS } from 'constants/hydraTests';

import { resetCancelOrReturnItems } from 'actions/account/account';
import { isAssigned } from 'actions/ab';
import {
  fetchAdditionalContractId,
  fetchSpecificReturnInfo,
  resetReturnsInState,
  returnSubmitComplete,
  returnSubmitError,
  returnSubmitInitiate,
  stepSelection,
  submitReturn,
  updateBoxInfoWithReturnReason
} from 'actions/account/returns';
import { fetchOrderDetails } from 'actions/account/order';
import {
  getNewAddressId,
  updateMailingAddressOrName,
  updateShippingPhone
} from 'actions/account/shipping';
import { PageLoader } from 'components/Loader';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import RefundSummary from 'components/account/Returns/RefundSummary';
import ReturnProductCards from 'components/account/Returns/ReturnProductCards';
import ShippingMethodChooser from 'components/account/Returns/ShippingMethodChooser';
import ReturnLegalVerbiage from 'components/account/Returns/ReturnLegalVerbiage';
import DoneSection from 'components/account/Returns/DoneSection';
import SubmitReturn from 'components/account/Returns/SubmitReturn';
import UPSBanner from 'components/account/UPSBanner';
import ReturnsAndExchanges from 'components/account/ReturnsAndExchanges';
import css from 'styles/containers/returns/returns.scss';

export class Returns extends Component {
  state = {
    labelDelivery: 'PRINT'
  }

  componentDidMount() {
    const {
      fetchSpecificReturnInfo,
      fetchOrderDetails,
      account: { cancelOrReturnItems: items },
      hydra6pmReturns
    } = this.props;

    const { router, marketplace: { account: { martyReturns } } } = this.context;

    if (!items.length) {
      router.push('/returns');
    }

    // if not permanent marty returns (zappos) or ab test 6pm returns
    if (!(martyReturns || hydra6pmReturns)) {
      router.push('/account');
    }

    const orderIds = [];

    items.forEach(({ orderId }) => {
      if (!orderIds.includes(orderId)) {
        orderIds.push(orderId);
        const orderItems = items.filter(order => order.orderId === orderId);
        fetchOrderDetails(orderId);

        const orderItemAsins = [];
        orderItems.forEach(({ product }) => {
          orderItemAsins.push(product.asin);
        });

        fetchSpecificReturnInfo(orderId, orderItemAsins);
      }
    });
  }

  componentWillUnmount() {
    const { resetCancelOrReturnItems, resetReturnsInState } = this.props;
    resetCancelOrReturnItems();
    resetReturnsInState();
  }

  onCancelReturn = () => {
    this.context.router.push('/account');
  }

  checkIfReturnAlreadySubmitted = currReturnItemsInOrder => response => {
    const { lineItems } = response;
    let hasReturnError = false; // variable to store error state if item was already returned

    for (let i = 0; i < currReturnItemsInOrder.length; i++) {
      const { product: { asin: currentItemAsin } } = currReturnItemsInOrder[i];
      // check for duplicate asins from items in fetchOrder call
      const currOrderBeingCheckedDupes = lineItems.filter(({ product: { asin } }) => asin === currentItemAsin);

      if (currOrderBeingCheckedDupes.length > 1) { // there are duplicate item asins in the fetchOrder order data
        // get duplicates of items from fetchOrder call that are ALSO returnable
        const currOrderDupesAndReturnable = currOrderBeingCheckedDupes.filter(({ product: { asin }, returnable }) => asin === currentItemAsin && returnable);
        // get duplicates of items being returned
        const currReturnItemDupes = currReturnItemsInOrder.filter(({ product: { asin } }) => asin === currentItemAsin);
        // there are MORE duplicate items being returned than valid number of returnable items in the fetchOrder data
        if (currReturnItemDupes.length > currOrderDupesAndReturnable.length) {
          hasReturnError = true;
          break; // poop out
        }
      } else { // item is UNIQUE in fetchOrder data, no need to count duplicates
        const { returnable: isUniqueItemReturnable } = currOrderBeingCheckedDupes[0];
        if (!isUniqueItemReturnable) {
          hasReturnError = true;
          break; // the item was already returned, poop out
        }
      }
    }
    return hasReturnError;
  }

  buildReturnInfoObject = (contractId, boxInfo, boxInfoIndex, addressId) => {
    const { items } = boxInfo[boxInfoIndex];
    const submissionItems = [];

    items.map(item => {
      const { unitId, reasonCode } = item;
      submissionItems.push({ unitId, reasonCode });
    });

    if (addressId) {
      return {
        contractId,
        submissionItems,
        returnLabelMethod: 'MAIL',
        addressId, // backend requires both addressId and snailMailAddressId for now
        snailMailAddressId: addressId,
        notSendNotifications: false
      };
    }
    return {
      contractId,
      submissionItems
    };
  }

  makeSubmitReturnCalls = (numOfBoxes, contractIds) => {
    const {
      returns: { boxInfo },
      submitReturn,
      stepSelection,
      returnSubmitComplete,
      returnSubmitError,
      getNewAddressId,
      shipping: {
        address
      }
    } = this.props;
    const ReturnsContainer = this;

    // this is where the magic happens, build returnSubmit object using contractIds + box object in state
    const returnCalls = function(addressId) {
      const returnSubmitCalls = [];

      for (let i = 0; i < numOfBoxes; i++) {
        returnSubmitCalls.push(submitReturn(ReturnsContainer.buildReturnInfoObject(contractIds[i], boxInfo, i + 1, addressId)));
      }
      Promise.all(returnSubmitCalls).then(() => {
        returnSubmitComplete();
        stepSelection('DONE');
      }).catch(() => {
        returnSubmitError();
      });
    };

    if (this.state.labelDelivery === 'MAIL') { // get new addressId first before submitting returns
      getNewAddressId(address, resp => {
        const { addressId } = resp || {};
        returnCalls(addressId);
      }, { forceOriginal: true });
    } else {
      returnCalls();
    }
  }

  onSubmitReturn = () => {
    const {
      account: { cancelOrReturnItems: items },
      returns: { contractIds, boxInfo },
      fetchOrderDetails,
      fetchAdditionalContractId,
      returnSubmitInitiate,
      returnSubmitError
    } = this.props;

    returnSubmitInitiate();
    window.scrollTo(0, 0);

    // There seem to be a good number of cases where items being returned are already in a return status.
    // Before submitting the return, let's make a call for the order details and compare the return status.
    const orderIdsToCheck = new Set();
    // Let's get all the orderIds of the items being returned and store them in Set
    items.forEach(({ orderId }) => orderIdsToCheck.add(orderId));

    const orderCheckPromises = [];
    // Compare items on a PER ORDER basis
    orderIdsToCheck.forEach(orderId => {
      const currReturnItemsInOrder = items.filter(returnItem => returnItem.orderId === orderId);

      orderCheckPromises.push(Promise.resolve(fetchOrderDetails(orderId)).then(
        this.checkIfReturnAlreadySubmitted(currReturnItemsInOrder)
      )); // end of orderCheckPromises array push
    }); // end of orderIdsToCheck forEach

    Promise.all(orderCheckPromises).then(hasReturnErrorValues => {
      if (hasReturnErrorValues.includes(true)) {
        // duplicate return was submitted, error out
        returnSubmitError();

      } else { // no errors proceed as normal
        const numOfBoxes = Object.keys(boxInfo).length;
        const numOfContractIds = contractIds.length;

        if (numOfBoxes > numOfContractIds) { // need to make calls for additional contractIds
          const { orderId, product: { asin } } = items[0]; // get random item orderId + asin
          const additionalContractIdCalls = [];

          for (let i = numOfContractIds; i < numOfBoxes; i++) { // keep calling endpoint to generate additional contractIds
            additionalContractIdCalls.push(fetchAdditionalContractId(orderId, asin));
          }
          Promise.all(additionalContractIdCalls).then(resp => {
            const allContractIds = contractIds.concat(resp);
            this.makeSubmitReturnCalls(numOfBoxes, allContractIds);
          });
        } else {
          this.makeSubmitReturnCalls(numOfBoxes, contractIds);
        }
      }
    });
  }

  onLabelDeliverySelect = labelDelivery => {
    this.setState({ labelDelivery });
  }

  onSelectReturnReason = ({ currentTarget }) => {
    const { updateBoxInfoWithReturnReason, returns: { boxInfo } } = this.props;
    const unitId = currentTarget.getAttribute('data-unitid');
    const boxNum = currentTarget.getAttribute('data-boxnum');
    const reasonCode = currentTarget.value;
    updateBoxInfoWithReturnReason(unitId, reasonCode, boxInfo, boxNum);
  }

  makeRefundSummary = (boxInfo, step) => {
    const boxEls = [];
    if (boxInfo) {
      const singleBox = Object.keys(boxInfo).length === 1;

      for (const box in boxInfo) {
        const { items } = boxInfo[box];
        boxEls.push(
          <div className={cn(css.cols, css.initiate)} key={box}>
            <div className={css.leftCol}>
              <ReturnProductCards
                step={step}
                boxNum={box}
                items={items}
                singleBox={singleBox}
                onSelectReturnReason={this.onSelectReturnReason} />
            </div>
            <div className={css.rightCol}>
              <RefundSummary
                items={items}
                boxNum={box}
                singleBox={singleBox} />
            </div>
          </div>
        );
      }
    }
    return boxEls;
  }

  makeErrorAlert = isReturnError => {
    if (isReturnError) {
      return (
        <ul className={css.boxErrorMsg}>
          <li>Oops! One or more of these items was already returned. Please <Link to="/returns">restart the return process</Link>.</li>
        </ul>
      );
    }
  }

  render() {
    const {
      order: { order },
      returns,
      shipping,
      updateMailingAddressOrName,
      updateShippingPhone
    } = this.props;

    const {
      step,
      boxInfo,
      postReturnInfo,
      isReturnError,
      isReturnSubmitting
    } = returns;

    const { labelDelivery } = this.state;

    const { marketplace: { account: { hideReturnsAndExchangesOnReturnConfirmation } } } = this.context;

    if (!order || isReturnSubmitting) {
      return <PageLoader centered={true}/>;
    }

    return (
      <SiteAwareMetadata>
        { step === 'DONE' &&
        <>
          <DoneSection postReturnInfo={postReturnInfo} boxInfo={boxInfo} />
          {!hideReturnsAndExchangesOnReturnConfirmation && (
            <div className={css.container}>
              <ReturnsAndExchanges/>
              { this.makeRefundSummary(boxInfo, step) }
            </div>
          )}
        </>
        }

        { step !== 'DONE' &&
        <>
          <div className={css.container}>

            <h1>Items you are returning</h1>
            { this.makeErrorAlert(isReturnError) }
            { this.makeRefundSummary(boxInfo, step) }

            <ReturnLegalVerbiage />
            <div className={css.labelDelivery}>
              <h2>How would you like to receive your label?</h2>
              <UPSBanner />
              <ShippingMethodChooser
                shipping={shipping}
                labelDelivery={labelDelivery}
                onLabelDeliverySelect={this.onLabelDeliverySelect}
                onCancelReturn={this.onCancelReturn}
                onSubmitReturn={this.onSubmitReturn}
                onUpdateMailingAddressOrName={updateMailingAddressOrName}
                onUpdateShippingPhone={updateShippingPhone} />
              { labelDelivery !== 'MAIL' &&
                <SubmitReturn
                  onCancelReturn={this.onCancelReturn}
                  onSubmitReturn={this.onSubmitReturn}
                  submitDisabled={isReturnSubmitting || isReturnError} /> }
            </div>
          </div>
        </>
        }
      </SiteAwareMetadata>
    );
  }
}

Returns.contextTypes = {
  router: PropTypes.object,
  marketplace: PropTypes.object
};

function mapStateToProps(state) {
  const { account, order, returns, shipping, holmes } = state;
  const customerId = holmes && holmes.customerId || '';

  return {
    account,
    order,
    returns,
    shipping,
    customerId,
    hydra6pmReturns: isAssigned(HYDRA_6PM_MARTY_RETURNS, 1, state)
  };
}

export default connect(mapStateToProps, {
  fetchSpecificReturnInfo,
  fetchOrderDetails,
  getNewAddressId,
  isAssigned,
  resetCancelOrReturnItems,
  stepSelection,
  submitReturn,
  updateMailingAddressOrName,
  updateShippingPhone,
  fetchAdditionalContractId,
  resetReturnsInState,
  returnSubmitInitiate,
  returnSubmitComplete,
  returnSubmitError,
  updateBoxInfoWithReturnReason
})(Returns);
